import { useEffect, useMemo, useState } from 'react';
// PRD §22 — GA4 dataLayer events for the consumer flow.
import { gaEvent } from '@/lib/analytics/ga4';
// PRD §11 Screen 1.75 — Photo & Film Notice (inline, no MUI dep).
import { PhotoFilmNoticeScreen } from './PhotoFilmNoticeScreen';
// PRD §18 — "Powered by Xotski" watermark below every game surface.
import { XotskiWatermark } from './games/XotskiWatermark';
// PRD §8 Tab 3 Look & Feel — celebration overlay played on prize reveal.
import { CelebrationOverlay } from '@/features/brand-studio/CelebrationOverlay';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { publicSurveyApi } from './api';
import type {
  BrandKit,
  PublicSurveyObject,
  XkStock,
  XyComponent,
  XyComponentOption,
} from '@/lib/api/types';
import { resolveGame } from './games/registry';
import { ClaimCodeCountdown } from './ClaimCodeCountdown';
import { cn } from '@/lib/cn';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { BRAND_DEFAULTS } from '@/lib/theme';
import { useT } from '@/lib/i18n/useT';

interface SurveyWizardProps {
  data: PublicSurveyObject;
  stationId: number;
  /**
   * PRD §11 — Manager preview mode. When `true`, the wizard renders
   * identically but `bulkSubmit` short-circuits client-side: NO request
   * hits the API, so xk_stock + xk_event_stock_station are NOT decremented
   * and NO xk_prize_redemption row is created. Used by the in-config
   * "Live preview" dialog. The wizard still walks through every screen so
   * the manager sees the real visual output.
   */
  dryRun?: boolean;
}

type ResponseValue = string;

// ── Form-controls theming derived from BrandKit ────────────────────────

const BORDER_RADIUS_PX: Record<NonNullable<BrandKit['inputBorderRadius']>, string> = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '16px',
  full: '9999px',
};

interface FormStyle {
  bg: string;
  borderColor: string;
  borderStyle: 'solid' | 'dashed' | 'none';
  borderRadius: string;
  text: string;
  label: string;
}

function deriveFormStyle(kit: BrandKit): FormStyle {
  const radiusKey = kit.inputBorderRadius ?? 'md';
  return {
    bg: kit.inputBgColor ?? 'rgba(255,255,255,0.10)',
    borderColor: kit.inputBorderColor ?? 'rgba(255,255,255,0.25)',
    borderStyle: kit.inputBorderStyle ?? 'solid',
    borderRadius: BORDER_RADIUS_PX[radiusKey],
    text: kit.inputTextColor ?? kit.fontColor ?? '#FFFFFF',
    label: kit.labelColor ?? 'rgba(255,255,255,0.7)',
  };
}

function readOptions(c: XyComponent): XyComponentOption[] {
  const src = c.initialization ?? c.options;
  if (!src) return [];
  if (Array.isArray(src)) {
    return src.filter((o) => o && typeof o === 'object' && 'value' in o) as XyComponentOption[];
  }
  return [];
}

export function SurveyWizard({ data, stationId, dryRun = false }: SurveyWizardProps) {
  const navigate = useNavigate();
  const t = useT();
  const [stepIdx, setStepIdx] = useState(0);
  const [responses, setResponses] = useState<Record<number, ResponseValue>>({});
  const [prize, setPrize] = useState<XkStock | null>(null);
  const [responseId, setResponseId] = useState<string | null>(null);
  // PRD §11 Screen 1.5 — when the kit configures an `ageGate`, this stores
  // the ISO timestamp of the moment the consumer affirmed it. We send it on
  // submit so the backend can persist `ldaVerifiedAt` for compliance. Per
  // PRD §13 we never capture the DOB itself.
  const [ldaVerifiedAt, setLdaVerifiedAt] = useState<string | null>(null);
  const ageVerified = !!ldaVerifiedAt;
  // PRD §11 Screen 1.75 — Photo & Film Notice gate. Renders only when the
  // kit toggles `photoFilmEnabled`; otherwise the wizard skips straight to
  // step 0. Notice, not consent, so a single Acknowledge flips the flag.
  const [photoFilmAck, setPhotoFilmAck] = useState(false);

  const kit: BrandKit = data.brandKit ?? ({} as BrandKit);
  const primary = kit.primaryColor ?? BRAND_DEFAULTS.primaryColor;
  // BUG-005 fix: prefer the explicit accentColor field (PRD §8 5-color),
  // fall back to legacy secondaryColor for kits that predate BrandKit v2.
  const accent = kit.accentColor ?? kit.secondaryColor ?? BRAND_DEFAULTS.accentColor;
  const fontFamily = kit.font ? `'${kit.font}', sans-serif` : undefined;
  const formStyle = useMemo(() => deriveFormStyle(kit), [kit]);

  // Caso 06 v1 — Multi-language. Source of truth precedence:
  //   1. `data.languages` + `data.defaultLanguage` (new top-level fields on
  //      xy_survey; the canonical signal from the manager).
  //   2. Translations maps on steps / components (legacy templates that
  //      ship translations JSON without the top-level columns).
  //   3. BsEvent.language='en-es' (very old templates with only *Translate
  //      columns and no JSON).
  const availableLanguages = useMemo(() => {
    const langs = new Set<string>();
    if (data.languages && data.languages.length > 0) {
      for (const l of data.languages) langs.add(l);
    }
    if (data.defaultLanguage) langs.add(data.defaultLanguage);
    // Always include 'en' as a baseline so legacy templates work.
    langs.add('en');
    for (const step of data.steps ?? []) {
      if (step.translations) {
        for (const k of Object.keys(step.translations)) langs.add(k);
      }
      for (const c of step.components ?? []) {
        if (c.translations) {
          for (const k of Object.keys(c.translations)) langs.add(k);
        }
      }
    }
    if (kit.language === 'en-es') langs.add('es');
    return Array.from(langs);
  }, [data.languages, data.defaultLanguage, data.steps, kit.language]);
  const supportsMultiLang = availableLanguages.length > 1;
  // Initial language: prefer the survey's declared default; otherwise the
  // browser's first supported locale; otherwise 'en'.
  const [lang, setLang] = useState<string>(() => {
    if (data.defaultLanguage && availableLanguages.includes(data.defaultLanguage)) {
      return data.defaultLanguage;
    }
    if (typeof navigator !== 'undefined') {
      const browser = (navigator.language ?? 'en').split('-')[0];
      if (availableLanguages.includes(browser)) return browser;
    }
    return 'en';
  });
  const isEs = supportsMultiLang && lang === 'es';

  // Per-event hidden components — set by the manager in Experience tab.
  const disabledIds = useMemo(
    () => new Set<number | string>(kit.customization?.disabledComponentIds ?? []),
    [kit.customization?.disabledComponentIds],
  );

  const submit = useMutation({
    mutationFn: (body: { stockId?: number }) => {
      // PRD §11 — Manager preview mode. Skip the API call entirely so
      // stocks, brand pool and redemptions stay untouched. Return a
      // synthetic shape that matches the real `bulkSubmit` response so
      // downstream handlers (PrizeReveal etc.) keep working.
      if (dryRun) {
        return Promise.resolve([
          {
            id: -1,
            // Sentinel responseId — the ClaimCodeCountdown poll will
            // detect this and skip its network request, so no claim
            // code row needs to exist.
            responseId: 'preview-dryrun',
          },
        ]);
      }
      return publicSurveyApi.bulkSubmit({
        surveyId: data.id,
        station: stationId,
        source: (data.productType as any) ?? 'xotski',
        date: new Date().toISOString(),
        stockId: body.stockId,
        // PRD §11 Screen 1.5 — only sent when the consumer actually saw the
        // age gate (kit.ageGate !== 'none'). Backend persists this on the
        // claim-code row for compliance audits; null/undefined is fine when
        // the gate was not configured.
        ...(ldaVerifiedAt ? { ldaVerifiedAt } : {}),
        responses: Object.entries(responses).map(([componentId, value]) => ({
          stepId:
            data.steps.find((s) =>
              s.components.some((c) => c.id === Number(componentId)),
            )?.id ?? 0,
          componentId: Number(componentId),
          value,
        })),
      });
    },
  });

  const steps = data.steps;
  const rawStep = steps[stepIdx];
  // Drop hidden components (the manager toggles in Experience).
  const visibleComponents = useMemo(
    () => (rawStep?.components ?? []).filter((c) => !disabledIds.has(c.id)),
    [rawStep, disabledIds],
  );
  const step = rawStep ? { ...rawStep, components: visibleComponents } : rawStep;
  const last = stepIdx === steps.length - 1;

  // Auto-skip a step that has no visible components left (everything was
  // disabled via customization).
  useEffect(() => {
    if (!step) return;
    if (visibleComponents.length > 0) return;
    if (last) return;
    setStepIdx((i) => i + 1);
  }, [step, visibleComponents.length, last]);

  // Phase 6: required validation across ALL component types (not just
  // inputText). A component is required when its validation JSON contains
  // `{key: 'isRequired'|'required', value: 'true'|true}`.
  const [triedAdvance, setTriedAdvance] = useState(false);
  function isComponentRequired(c: XyComponent): boolean {
    const v = c.validation;
    if (!v) return false;
    const arr = Array.isArray(v) ? v : [];
    return arr.some(
      (r: any) =>
        r &&
        (r.key === 'isRequired' || r.key === 'required') &&
        (r.value === 'true' || r.value === true),
    );
  }
  function isComponentAnswered(c: XyComponent): boolean {
    const raw = responses[c.id];
    if (raw == null) return false;
    // For multi-selects (check) we store CSV; empty string = not answered.
    return String(raw).trim().length > 0;
  }
  // PRD §11 Screen 3 — InputText fields whose label/placeholder mentions
  // "email" / "correo" must look like a real address before the user can
  // advance. We use a permissive regex (RFC 5322 is too strict for the
  // average consumer's address) and only validate when the field has
  // content — the required-empty case is already caught above.
  const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  function isEmailComponent(c: XyComponent): boolean {
    if (c.type !== 'inputText') return false;
    const haystack = `${c.label ?? ''} ${(c as any).placeholder ?? ''}`
      .toLowerCase();
    return (
      haystack.includes('email') ||
      haystack.includes('e-mail') ||
      haystack.includes('correo')
    );
  }
  const requiredUnanswered = (visibleComponents ?? []).filter(
    (c) => isComponentRequired(c) && !isComponentAnswered(c),
  );
  const invalidEmails = (visibleComponents ?? []).filter((c) => {
    if (!isEmailComponent(c)) return false;
    const raw = String(responses[c.id] ?? '').trim();
    if (!raw) return false; // empty → covered by required check (or optional)
    return !EMAIL_RX.test(raw);
  });
  const canAdvance =
    requiredUnanswered.length === 0 && invalidEmails.length === 0;

  const next = () => {
    if (!canAdvance) {
      setTriedAdvance(true);
      return;
    }
    setTriedAdvance(false);
    if (last) {
      submit.mutate({}, { onSuccess: () => navigate('/public-finish') });
      return;
    }
    setStepIdx(stepIdx + 1);
  };

  const handleWin = (item: { id: number }) => {
    submit.mutate(
      { stockId: item.id },
      {
        onSuccess: (rows) => {
          // PRD §11 Screen 5 — keep the responseId UUID so the claim-code
          // countdown can poll /xk-prize-redemption/by-response/:id.
          if (Array.isArray(rows) && rows[0]?.responseId) {
            setResponseId(rows[0].responseId);
            gaEvent('claim_code_shown', {
              surveyId: data.id,
              stationId,
              responseId: rows[0].responseId,
            });
          }
        },
      },
    );
    const prize = data.stocks.find((s) => s.id === item.id) ?? null;
    setPrize(prize);
    gaEvent('prize_won', {
      surveyId: data.id,
      stationId,
      stockId: item.id,
      prizeName: prize?.name,
    });
  };

  // PRD §22 — fire survey_start once per session so GA can attribute the
  // funnel. Effect runs after the BrandKit/steps query has resolved so we
  // can stamp the surveyId on the event.
  useEffect(() => {
    if (!data?.id) return;
    gaEvent('survey_start', { surveyId: data.id, stationId });
    // We intentionally don't depend on stationId — once-per-mount is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  // Age gate (if configured and we're on the first step)
  if (kit.ageGate && kit.ageGate !== 'none' && !ageVerified && stepIdx === 0) {
    return (
      <PublicFrame primary={primary} accent={accent} fontFamily={fontFamily} kit={kit}>
        <div className="space-y-6 text-center">
          <p className="text-xs uppercase tracking-widest opacity-60">
            {t('publicSurvey.ageGate.eyebrow')}
          </p>
          <h1 className="text-3xl font-bold leading-tight">
            {t('publicSurvey.ageGate.question', { age: kit.ageGate.replace('+', '') })}
          </h1>
          <p className="opacity-70">{t('publicSurvey.ageGate.body')}</p>
          <div className="flex justify-center gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                const ts = new Date().toISOString();
                setLdaVerifiedAt(ts);
                gaEvent('lda_verified', {
                  surveyId: data.id,
                  stationId,
                  ageGate: kit.ageGate ?? 'none',
                });
              }}
              className="rounded-full px-6 py-3 text-sm font-bold"
              style={{ backgroundColor: accent, color: primary }}
            >
              {t('publicSurvey.ageGate.yes')}
            </button>
            <button
              type="button"
              onClick={() => window.location.replace('/')}
              className="rounded-full border border-white/30 px-6 py-3 text-sm font-bold"
            >
              {t('publicSurvey.ageGate.no')}
            </button>
          </div>
        </div>
      </PublicFrame>
    );
  }

  // PRD §11 Screen 1.75 — Photo & Film Notice. Render only when the kit
  // enabled it AND the user hasn't acknowledged this session yet. Comes
  // strictly after the LDA age gate (already verified or absent) so
  // attendees never see two prompts in a row.
  if (
    kit.customization?.photoFilmEnabled &&
    !photoFilmAck &&
    stepIdx === 0 &&
    (!kit.ageGate || kit.ageGate === 'none' || ageVerified)
  ) {
    return (
      <PublicFrame primary={primary} accent={accent} fontFamily={fontFamily} kit={kit}>
        <PhotoFilmNoticeScreen
          onAcknowledge={() => setPhotoFilmAck(true)}
          accent={accent}
          primary={primary}
          brandName={kit.campaignName ?? null}
        />
      </PublicFrame>
    );
  }

  // Has prize → show reveal screen
  if (prize) {
    // PRD §11 Screen 6 — post-claim redirect. Default to the Onxite finish
    // screen; the manager can override per-event in Brand Studio Tab 6.
    const redirectMode = kit.customization?.postClaimRedirect ?? 'none';
    const redirectUrl = kit.customization?.postClaimRedirectUrl ?? null;
    const handleContinue = () => {
      if (redirectMode === 'external' && redirectUrl) {
        // External redirect takes the browser out of Xotski entirely
        // (sponsor microsite, ecommerce, etc.). Force a top-level navigation
        // so the SPA history doesn't trap the user.
        window.location.assign(redirectUrl);
        return;
      }
      if (redirectMode === 'branded') {
        // Branded mode keeps the user in the Xotski surface but shows a
        // brand-themed thank-you frame — PrizeReveal renders that inline,
        // so all we need here is a no-op (the button is hidden).
        return;
      }
      // Default: Onxite finish screen.
      navigate('/public-finish');
    };
    return (
      <PublicFrame primary={primary} accent={accent} fontFamily={fontFamily} kit={kit}>
        <PrizeReveal
          prize={prize}
          accent={accent}
          primary={primary}
          theme={kit.celebrationTheme}
          onContinue={handleContinue}
          responseId={responseId ?? undefined}
          redirectMode={redirectMode}
          deliveryMode={kit.deliveryMode ?? undefined}
        />
      </PublicFrame>
    );
  }

  // Step name + description with translate fallback (same rule the ox-design-system
  // ConsumerSurveyWizard uses).
  // Phase 4: translations lookup with legacy *Translate fallback.
  // Priority: translations[lang]?.field → step.<legacy>Translate (es-only) → step.<field>
  const stepTr = lang !== 'en' ? step.translations?.[lang] : undefined;
  const stepName =
    stepTr?.name ?? (isEs && step.nameTranslate ? step.nameTranslate : step.name);
  const stepDescTranslate =
    (step as { descriptionTranslate?: string | null }).descriptionTranslate ?? null;
  const stepDesc =
    stepTr?.description ??
    (isEs && stepDescTranslate ? stepDescTranslate : step.description ?? null);

  // Any registered game inside this step → suppress the generic "Continue"
  // button (games own their own action button).
  const hasGame = step.components.some(
    (c) =>
      c.type === 'gameWheel' ||
      c.type === 'gameScratch' ||
      c.type === 'gamePlinko' ||
      c.type === 'gamePirinola' ||
      c.type === 'gameSlots' ||
      c.type === 'gameRandomDraw' ||
      c.type === 'gameSoccer' ||
      c.type === 'gameLoteria' ||
      (c.type === 'specialType' &&
        typeof c.specialField === 'string' &&
        c.specialField.endsWith('Component')),
  );

  return (
    <PublicFrame primary={primary} accent={accent} fontFamily={fontFamily} kit={kit}>
      {/* Top bar: progress dots stay centered to the viewport; the language
          picker (when shown) overlays at the right edge so it never pushes
          the dots off-center. */}
      <div className="relative mb-6 flex h-7 items-center justify-center">
        <div className="flex gap-1.5">
          {steps.map((s, i) => (
            <span
              key={s.id}
              className={cn(
                'h-1.5 w-8 rounded-full',
                i <= stepIdx ? '' : 'opacity-30',
              )}
              style={{ backgroundColor: accent }}
            />
          ))}
        </div>
        {supportsMultiLang && (
          <div className="absolute right-0 inline-flex overflow-hidden rounded-full border border-white/20 text-[10px] font-bold">
            {availableLanguages.map((l) => {
              const active = lang === l;
              return (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLang(l)}
                  aria-pressed={active}
                  className={cn(
                    'px-2.5 py-0.5 uppercase tracking-wider transition-colors',
                    active ? '' : 'opacity-60 hover:opacity-100',
                  )}
                  style={
                    active ? { background: accent, color: primary } : undefined
                  }
                >
                  {l}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {stepName && (
          <h1 className="text-center text-2xl font-bold leading-tight">{stepName}</h1>
        )}
        {stepDesc && (
          <p className="text-center text-sm opacity-70">{stepDesc}</p>
        )}
        <div className="space-y-4">
          {step.components.map((c) => {
            const isInvalidEmail =
              triedAdvance && invalidEmails.some((e) => e.id === c.id);
            return (
              <ComponentRenderer
                key={c.id}
                component={c}
                value={responses[c.id] ?? ''}
                onChange={(v) => setResponses((r) => ({ ...r, [c.id]: v }))}
                onWheelWin={handleWin}
                stocks={data.stocks}
                primary={primary}
                accent={accent}
                formStyle={formStyle}
                isEs={isEs}
                kit={kit}
                setLang={setLang}
                lang={lang}
                emailError={isInvalidEmail}
              />
            );
          })}
        </div>
        {!hasGame && (
          <div className="flex flex-col items-center gap-2 pt-4">
            {triedAdvance && requiredUnanswered.length > 0 && (
              <p className="text-center text-xs font-semibold opacity-80" style={{ color: accent }}>
                {isEs
                  ? `Por favor responde los campos requeridos (${requiredUnanswered.length}).`
                  : `Please answer the required field${requiredUnanswered.length === 1 ? '' : 's'} (${requiredUnanswered.length}).`}
              </p>
            )}
            {triedAdvance && invalidEmails.length > 0 && (
              <p className="text-center text-xs font-semibold opacity-80" style={{ color: accent }}>
                {isEs
                  ? 'Revisa el correo — debe tener el formato nombre@dominio.com.'
                  : 'Check the email — it must look like name@domain.com.'}
              </p>
            )}
            <button
              type="button"
              onClick={next}
              disabled={!canAdvance && triedAdvance}
              className="rounded-full px-8 py-3 text-base font-bold transition-opacity disabled:opacity-60"
              style={{ backgroundColor: accent, color: primary }}
            >
              {last ? t('publicSurvey.finish') : t('publicSurvey.continueArrow')}
            </button>
          </div>
        )}
      </div>
    </PublicFrame>
  );
}

interface FrameProps {
  primary: string;
  accent: string;
  fontFamily?: string;
  kit: BrandKit;
  children: React.ReactNode;
}

function PublicFrame({ primary, accent, fontFamily, kit, children }: FrameProps) {
  // PRD §8 Tab 1 — 5-color palette: `backgroundColor` is the explicit
  // background field of the brand kit. The mockup uses `value.backgroundColor
  // ?? primary`; mirror that so primary and background can be set
  // independently. Falling back to `primary` preserves legacy behaviour for
  // kits that never defined a backgroundColor.
  const frameBg = kit.backgroundColor ?? primary;
  return (
    <div
      className="min-h-screen text-white"
      style={{
        backgroundColor: frameBg,
        fontFamily,
        backgroundImage: kit.backgroundImage ? `url(${resolveImageUrl(kit.backgroundImage)})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center p-6">
        {/* BUG fix — mockup (SurveyPreview) used `brandLogo ?? clientLogo`
            but the live wizard only rendered `clientLogo`. When a brand
            uploads brandLogo only, the consumer flow showed nothing.
            Mirror the mockup's chain so both surfaces match. */}
        {(kit.brandLogo || kit.clientLogo) && (
          <img
            src={resolveImageUrl(kit.brandLogo ?? kit.clientLogo!)}
            alt=""
            className="mb-8 h-12 object-contain"
          />
        )}
        <div className="w-full">{children}</div>
      </div>
      {/* hidden span just to silence accent unused */}
      <span aria-hidden style={{ color: accent }} className="sr-only">
        accent
      </span>
    </div>
  );
}

/**
 * PRD §11 Screen 3 — Inline consent block with UNBUNDLED checkboxes.
 *
 * Two separate consent acts (per PRD §13 lane discipline — marketing must
 * never piggyback on T&C acceptance):
 *   1. Terms & Conditions — REQUIRED. Without this, the wizard can't advance.
 *   2. Marketing opt-in    — OPTIONAL. Captures permission to email later.
 *
 * Encoding (single response slot, backward-compatible):
 *   • ''                              → not answered (legacy + new)
 *   • 'true'                          → terms accepted (legacy)
 *   • 'terms=true'                    → terms accepted (new, marketing untouched)
 *   • 'terms=true&marketing=true'     → terms + marketing both accepted
 *   • 'terms=true&marketing=false'    → terms accepted, marketing explicitly declined
 *
 * The required-validation logic (`String(raw).trim().length > 0`) treats
 * either 'true' or 'terms=true...' as answered. Marketing is parsed out for
 * downstream consumers; the variant prop selects whether to render it.
 */
function parseConsentValue(value: string): { terms: boolean; marketing: boolean | null } {
  if (!value) return { terms: false, marketing: null };
  if (value === 'true') return { terms: true, marketing: null };
  const parts = new URLSearchParams(value);
  return {
    terms: parts.get('terms') === 'true',
    marketing: parts.get('marketing') === 'true'
      ? true
      : parts.get('marketing') === 'false'
        ? false
        : null,
  };
}

function encodeConsentValue(terms: boolean, marketing: boolean | null): string {
  if (!terms) return '';
  if (marketing == null) return 'terms=true';
  return `terms=true&marketing=${marketing ? 'true' : 'false'}`;
}

function ConsentBlock({
  kit,
  isEs,
  value,
  onChange,
  accent,
  primary,
  formStyle,
}: {
  kit: BrandKit;
  isEs: boolean;
  value: string;
  onChange: (v: string) => void;
  accent: string;
  primary: string;
  formStyle: FormStyle;
}) {
  const [open, setOpen] = useState(false);
  const parsed = parseConsentValue(value);

  // Variant A (anonymous) hides the marketing opt-in entirely — there's no
  // PII slot to email anyway. Variant B (default) renders both checkboxes.
  const variant: 'A' | 'B' =
    (kit.customization?.consentVariant as 'A' | 'B' | undefined) ?? 'B';
  const showMarketing = variant === 'B';

  const intro =
    kit.consentWording ||
    (isEs
      ? 'Tus respuestas son anónimas. Al continuar aceptas nuestros términos de servicio.'
      : 'Your responses are anonymous. By continuing, you agree to our terms of service.');

  const handleTermsToggle = (next: boolean) => {
    onChange(encodeConsentValue(next, parsed.marketing));
  };

  const handleMarketingToggle = (next: boolean) => {
    onChange(encodeConsentValue(parsed.terms, next));
  };

  return (
    <div className="space-y-3">
      <p
        className="text-sm leading-relaxed"
        style={{ color: formStyle.text, opacity: 0.85 }}
      >
        {intro}
      </p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm font-bold underline-offset-2 hover:underline"
        style={{ color: accent }}
      >
        {isEs ? 'Leer Términos y Condiciones' : 'Read Terms & Conditions'}
      </button>
      <label
        className="flex cursor-pointer items-start gap-2 text-sm"
        style={{ color: formStyle.text }}
      >
        <input
          type="checkbox"
          checked={parsed.terms}
          onChange={(e) => handleTermsToggle(e.target.checked)}
          className="mt-0.5 h-4 w-4"
          style={{ accentColor: accent }}
        />
        <span>
          {isEs
            ? 'Acepto los Términos y Condiciones'
            : 'I agree to the Terms & Conditions'}
          <span className="ml-1" style={{ color: accent }}>*</span>
        </span>
      </label>

      {showMarketing && (
        <label
          className="flex cursor-pointer items-start gap-2 text-sm"
          style={{ color: formStyle.text }}
        >
          <input
            type="checkbox"
            checked={parsed.marketing === true}
            onChange={(e) => handleMarketingToggle(e.target.checked)}
            className="mt-0.5 h-4 w-4"
            style={{ accentColor: accent }}
          />
          <span>
            {isEs
              ? 'Quiero recibir noticias y ofertas de esta marca por correo (opcional)'
              : 'I want to receive brand updates and offers by email (optional)'}
          </span>
        </label>
      )}

      {open && (
        <ConsentModal isEs={isEs} onClose={() => setOpen(false)} accent={accent} primary={primary} />
      )}
    </div>
  );
}

/**
 * Standard T&C modal with the boilerplate Onxite copy (same content the
 * design-system `ConsentDataCollection` uses). Closes on overlay click and on
 * the explicit Close button.
 */
function ConsentModal({
  isEs,
  onClose,
  accent,
  primary,
}: {
  isEs: boolean;
  onClose: () => void;
  accent: string;
  primary: string;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white text-[#111]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="text-base font-bold">
            {isEs ? 'Términos y Condiciones' : 'Terms & Conditions'}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900"
          >
            ✕
          </button>
        </div>
        <div className="space-y-3 overflow-y-auto px-5 py-4 text-sm leading-relaxed">
          {isEs ? (
            <>
              <p>
                <strong>Términos y Condiciones Generales de Onxite</strong>
              </p>
              <p>
                Onxite proporciona una plataforma digital para apoyar el registro
                en eventos, encuestas y el seguimiento de experiencias de marca.
                Al registrarse o completar una encuesta mediante Onxite, usted
                acepta estos términos.
              </p>
              <p>
                <strong>Uso de la información:</strong> Sus respuestas pueden ser
                utilizadas con fines estadísticos y de análisis. La información
                personal identificable, si es solicitada, será tratada con
                confidencialidad y bajo nuestra Política de Privacidad.
              </p>
              <p>
                <strong>Retiro del consentimiento:</strong> Puedes retirar tu
                consentimiento en cualquier momento contactando a tu marca
                organizadora.
              </p>
            </>
          ) : (
            <>
              <p>
                <strong>Onxite – General Terms &amp; Conditions</strong>
              </p>
              <p>
                Onxite provides a digital platform to support event check-ins,
                surveys, and brand experience tracking. By checking in to this
                experience or submitting a survey via Onxite, you agree to these
                terms.
              </p>
              <p>
                <strong>Use of information:</strong> Your responses may be used
                for statistical and analytical purposes. Personally identifiable
                information, if collected, will be treated as confidential and
                handled according to our Privacy Policy.
              </p>
              <p>
                <strong>Withdrawing consent:</strong> You may withdraw your
                consent at any time by contacting your organizing brand.
              </p>
            </>
          )}
        </div>
        <div className="border-t border-gray-200 px-5 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-5 py-2 text-sm font-bold"
            style={{ background: accent, color: primary }}
          >
            {isEs ? 'Cerrar' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ComponentRendererProps {
  component: XyComponent;
  value: string;
  onChange: (v: string) => void;
  onWheelWin: (item: { id: number }) => void;
  stocks: PublicSurveyObject['stocks'];
  primary: string;
  accent: string;
  formStyle: FormStyle;
  isEs: boolean;
  kit: BrandKit;
  setLang: (l: string) => void;
  lang: string;
  /**
   * PRD §11 Screen 3 — set when the component is an email field whose
   * current value fails the format check. The renderer paints a red
   * border + shows the inline message so the consumer sees the problem
   * next to the field, not just in the bottom error banner.
   */
  emailError?: boolean;
}

/** Inline style for an input/select/pill wrapper using the form-controls tokens. */
function inputBoxStyle(fs: FormStyle): React.CSSProperties {
  return {
    backgroundColor: fs.bg,
    borderColor: fs.borderColor,
    borderStyle: fs.borderStyle,
    borderWidth: fs.borderStyle === 'none' ? 0 : 1,
    borderRadius: fs.borderRadius,
    color: fs.text,
  };
}

function ComponentRenderer({
  component,
  value,
  onChange,
  onWheelWin,
  stocks,
  primary,
  accent,
  formStyle,
  isEs,
  kit,
  setLang,
  lang,
  emailError,
}: ComponentRendererProps) {
  const c = component;
  // Phase 4: prefer translations[lang] when present; fall back to labelTranslate
  // (es-only legacy) then the base label.
  const compTr = lang !== 'en' ? c.translations?.[lang] : undefined;
  const label =
    compTr?.label ?? (isEs && c.labelTranslate ? c.labelTranslate : c.label) ?? c.type;
  // If translations carries `options`, use those (already translated); else fall
  // back to component.initialization with per-option `translate` field.
  const opts = compTr?.options ?? readOptions(c);
  const optLabel = (o: { value: string; translate?: string }) =>
    isEs && o.translate ? o.translate : o.value;

  // Game — look up the renderer in the registry. Priority:
  //   1. kit.gameMechanic (BsEvent overlay — what the manager picked in the
  //      Survey Configuration → Game tab). Lets the same template render
  //      different mechanics per event without re-cloning the survey tree.
  //   2. The component's specialField / type (template-level choice).
  // Falls through to the next renderer if no game matches.
  const GameComponent = resolveGame({
    gameCode: kit.gameMechanic,
    specialField: c.specialField,
    componentType: c.type,
  });
  if (
    GameComponent &&
    (c.type === 'gameWheel' ||
      c.type === 'gameScratch' ||
      c.type === 'gamePlinko' ||
      c.type === 'gamePirinola' ||
      c.type === 'gameSlots' ||
      c.type === 'gameRandomDraw' ||
      c.type === 'gameSoccer' ||
      c.type === 'gameLoteria' ||
      (c.type === 'specialType' &&
        typeof c.specialField === 'string' &&
        c.specialField.endsWith('Component')))
  ) {
    const cta =
      (isEs ? kit.wheelTaglineEs : kit.wheelTaglineEn) ?? undefined;
    return (
      <div className="space-y-3">
        {cta && (
          <p className="text-center text-base font-semibold opacity-90">{cta}</p>
        )}
        <GameComponent
          items={stocks}
          primary={primary}
          accent={accent}
          lang={isEs ? 'es' : 'en'}
          logo={kit.brandLogo ?? kit.clientLogo}
          onWin={onWheelWin}
        />
        {/* PRD §18 — universal watermark below every game mechanic. */}
        <XotskiWatermark />
      </div>
    );
  }

  // Consent (either consentForm type or specialType + consentStandard).
  if (
    c.type === 'consentForm' ||
    (c.type === 'specialType' && c.specialField === 'consentStandard')
  ) {
    return (
      <ConsentBlock
        kit={kit}
        isEs={isEs}
        value={value}
        onChange={onChange}
        accent={accent}
        primary={primary}
        formStyle={formStyle}
      />
    );
  }

  if (c.type === 'welcomeScreen') {
    return <p className="text-center opacity-80" style={{ color: formStyle.text }}>{label}</p>;
  }

  if (c.type === 'completionScreen') {
    return (
      <div className="flex flex-col items-center gap-2 py-4">
        <span
          className="grid h-14 w-14 place-items-center rounded-full text-2xl"
          style={{ background: accent, color: primary }}
        >
          ✓
        </span>
        <p className="font-bold" style={{ color: formStyle.text }}>{label}</p>
      </div>
    );
  }

  if (c.type === 'descriptionText') {
    return (
      <p className="text-sm leading-relaxed" style={{ color: formStyle.text }}>
        {label}
      </p>
    );
  }

  if (c.type === 'languageSelector') {
    return (
      <div className="flex flex-col items-center gap-3 py-2">
        <p className="text-sm font-bold" style={{ color: formStyle.text }}>
          {label || (isEs ? 'Elegí tu idioma' : 'Pick your language')}
        </p>
        <div className="flex gap-2">
          {(['en', 'es'] as const).map((l) => {
            const active = lang === l;
            return (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                aria-pressed={active}
                className="px-4 py-2 text-sm font-bold border"
                style={{
                  ...inputBoxStyle(formStyle),
                  backgroundColor: active ? accent : formStyle.bg,
                  color: active ? primary : formStyle.text,
                }}
              >
                {l.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (c.type === 'inputText' || c.type === 'inputNumber' || c.type === 'inputDate') {
    // PRD §11 Screen 3 — heuristic email detection (label/placeholder
    // contains "email"/"correo"). Switches the input to type=email so the
    // mobile keyboard adapts AND so the browser's built-in hint kicks in;
    // the SurveyWizard parent still owns the canonical regex gate.
    const labelStr = `${c.label ?? ''} ${(c as any).placeholder ?? ''}`
      .toLowerCase();
    const isEmailLike =
      c.type === 'inputText' &&
      (labelStr.includes('email') ||
        labelStr.includes('e-mail') ||
        labelStr.includes('correo'));
    const inputType =
      c.type === 'inputNumber'
        ? 'number'
        : c.type === 'inputDate'
          ? 'date'
          : isEmailLike
            ? 'email'
            : 'text';
    const boxStyle = inputBoxStyle(formStyle);
    if (emailError) {
      boxStyle.borderColor = '#ef4444'; // tailwind red-500 — consistent with toast/error palette
      boxStyle.borderStyle = 'solid';
      boxStyle.borderWidth = 1;
    }
    return (
      <label className="block">
        <span
          className="mb-2 block text-xs uppercase tracking-wide"
          style={{ color: formStyle.label }}
        >
          {label}
        </span>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          type={inputType}
          inputMode={isEmailLike ? 'email' : undefined}
          autoComplete={isEmailLike ? 'email' : undefined}
          className="h-12 w-full px-4 text-sm focus:outline-none"
          style={boxStyle}
          aria-invalid={emailError ? 'true' : undefined}
        />
        {emailError && (
          <span
            className="mt-1 block text-[11px] font-semibold"
            style={{ color: '#ef4444' }}
          >
            {isEs
              ? 'Formato no válido — usa nombre@dominio.com'
              : 'Invalid format — use name@domain.com'}
          </span>
        )}
      </label>
    );
  }

  if (c.type === 'numberRange') {
    const numeric = Number(value) || 50;
    return (
      <label className="block">
        <span
          className="mb-2 block text-xs uppercase tracking-wide"
          style={{ color: formStyle.label }}
        >
          {label}
        </span>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={100}
            value={numeric}
            onChange={(e) => onChange(e.target.value)}
            className="h-2 flex-1 accent-white"
            style={{ accentColor: accent }}
          />
          <span
            className="w-10 text-right text-sm tabular-nums"
            style={{ color: formStyle.text }}
          >
            {numeric}
          </span>
        </div>
      </label>
    );
  }

  if (c.type === 'singleCheck') {
    const list = opts.length ? opts : [{ key: '1', value: 'Option 1' }];
    return (
      <fieldset className="space-y-2">
        <legend
          className="mb-1 block text-xs uppercase tracking-wide"
          style={{ color: formStyle.label }}
        >
          {label}
        </legend>
        <div className="space-y-2">
          {list.map((o) => {
            const v = o.value;
            const active = value === v;
            return (
              <button
                key={o.key ?? v}
                type="button"
                onClick={() => onChange(v)}
                aria-pressed={active}
                className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm border transition-colors"
                style={{
                  ...inputBoxStyle(formStyle),
                  ...(active
                    ? { background: accent, color: primary, borderColor: accent }
                    : {}),
                }}
              >
                <span
                  className="grid h-4 w-4 place-items-center rounded-full border"
                  style={{ borderColor: active ? primary : formStyle.borderColor }}
                >
                  {active && (
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ background: primary }}
                    />
                  )}
                </span>
                <span>{optLabel(o)}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (c.type === 'check') {
    // Multi-select stored as comma-separated string for simplicity.
    const selected = new Set(value ? value.split(',') : []);
    const list = opts.length ? opts : [{ key: '1', value: 'Option A' }];
    return (
      <fieldset className="space-y-2">
        <legend
          className="mb-1 block text-xs uppercase tracking-wide"
          style={{ color: formStyle.label }}
        >
          {label}
        </legend>
        <div className="space-y-2">
          {list.map((o) => {
            const v = o.value;
            const active = selected.has(v);
            return (
              <button
                key={o.key ?? v}
                type="button"
                onClick={() => {
                  const next = new Set(selected);
                  if (active) next.delete(v);
                  else next.add(v);
                  onChange(Array.from(next).join(','));
                }}
                aria-pressed={active}
                className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm border"
                style={{
                  ...inputBoxStyle(formStyle),
                  ...(active
                    ? { background: accent, color: primary, borderColor: accent }
                    : {}),
                }}
              >
                <span
                  className="grid h-4 w-4 place-items-center border text-[10px] font-bold"
                  style={{
                    borderColor: active ? primary : formStyle.borderColor,
                    borderRadius: 4,
                    color: primary,
                  }}
                >
                  {active ? '✓' : ''}
                </span>
                <span>{optLabel(o)}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (c.type === 'switchCheck') {
    const on = value === 'true';
    return (
      <label className="flex items-center justify-between gap-3 py-2" style={{ color: formStyle.text }}>
        <span className="text-sm">{label}</span>
        <button
          type="button"
          onClick={() => onChange(on ? '' : 'true')}
          aria-pressed={on}
          className="relative inline-block h-6 w-11 transition-colors border"
          style={{
            ...inputBoxStyle(formStyle),
            background: on ? accent : formStyle.bg,
            borderRadius: '9999px',
          }}
        >
          <span
            className="absolute top-[2px] h-4 w-4 rounded-full bg-white transition-all"
            style={{ left: on ? 22 : 2 }}
          />
        </button>
      </label>
    );
  }

  if (c.type === 'select' || c.type === 'tagSelect') {
    return (
      <label className="block">
        <span
          className="mb-2 block text-xs uppercase tracking-wide"
          style={{ color: formStyle.label }}
        >
          {label}
        </span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-12 w-full px-3 text-sm appearance-none focus:outline-none"
          style={inputBoxStyle(formStyle)}
        >
          <option value="" disabled>
            {isEs ? 'Seleccionar…' : 'Select…'}
          </option>
          {opts.map((o) => (
            <option key={o.key ?? o.value} value={o.value}>
              {optLabel(o)}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (c.type === 'uploadPhoto' || c.type === 'selectPhoto') {
    // Simple URL input fallback. The real uploader lives in the manager UI;
    // the public flow stores the chosen image's URL in `value`.
    return (
      <label className="block">
        <span
          className="mb-2 block text-xs uppercase tracking-wide"
          style={{ color: formStyle.label }}
        >
          {label}
        </span>
        <div className="flex items-center gap-3">
          {value && (
            <img src={resolveImageUrl(value)} alt="" className="h-14 w-14 rounded-lg object-cover" />
          )}
          <input
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://…"
            className="h-12 flex-1 px-4 text-sm focus:outline-none"
            style={inputBoxStyle(formStyle)}
          />
        </div>
      </label>
    );
  }

  // Default fallback — surface the type so unmapped components are still
  // identifiable instead of silently rendering as input.
  return (
    <label className="block">
      <span
        className="mb-2 block text-xs uppercase tracking-wide"
        style={{ color: formStyle.label }}
      >
        {label} · {c.type}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full px-4 text-sm focus:outline-none"
        style={inputBoxStyle(formStyle)}
      />
    </label>
  );
}

interface PrizeRevealProps {
  prize: XkStock;
  accent: string;
  primary: string;
  theme?: BrandKit['celebrationTheme'];
  onContinue: () => void;
  /** Survey response UUID; enables the live claim-code + countdown UI. */
  responseId?: string;
  /**
   * PRD §11 Screen 6 — what happens when the user taps Continue after the
   * claim-code reveal. Drives the CTA label + behaviour:
   *   • 'none'     → "Continue" navigates to the Onxite finish screen
   *   • 'branded'  → CTA is hidden; the prize panel itself is the terminal
   *                  branded thank-you (BA / kiosk can reset to the splash)
   *   • 'external' → "Continue to {brand}" hands off to the brand microsite
   */
  redirectMode?: 'none' | 'branded' | 'external';
  /**
   * PRD §11 — Prize delivery mode. When 'off' the BA hands the item
   * directly and Xotski doesn't generate / track a claim code, so the
   * countdown UI is skipped entirely.
   */
  deliveryMode?: 'instant' | 'voucher' | 'off' | null;
}

function PrizeReveal({
  prize,
  accent,
  primary,
  theme,
  onContinue,
  responseId,
  redirectMode = 'none',
  deliveryMode,
}: PrizeRevealProps) {
  const t = useT();
  const ctaLabel =
    redirectMode === 'external'
      ? t('publicSurvey.prizeReveal.continueExternal')
      : t('common.continue');
  // PRD §8 Tab 3 — celebration mounts when the prize panel renders and
  // self-dismisses after ~2.5 s. `theme='none'` (or undefined) skips
  // rendering, keeping the reveal silent for brands that opted out.
  const showCelebration = theme && theme !== 'none';
  return (
    <div className="space-y-6 text-center">
      {showCelebration && <CelebrationOverlay theme={theme} accent={accent} />}
      <div className="mx-auto h-32 w-32 rounded-full" style={{ backgroundColor: accent }}>
        <div className="grid h-full w-full place-items-center">
          {prize.image ? (
            <img src={resolveImageUrl(prize.image)} alt="" className="h-24 w-24 rounded-full object-contain" />
          ) : (
            <span className="font-display text-4xl" style={{ color: primary }}>
              🎉
            </span>
          )}
        </div>
      </div>
      <p className="text-xs uppercase tracking-widest opacity-60">
        {t('publicSurvey.wheel.youWon')}
      </p>
      <h1 className="text-3xl font-bold">{prize.name}</h1>
      {deliveryMode === 'off' ? (
        // No claim code: BA delivers prize directly. Show a soft hint so
        // the consumer knows what to do next without the countdown.
        <p className="opacity-70">
          {t('publicSurvey.prizeReveal.directDelivery')}
        </p>
      ) : responseId ? (
        <ClaimCodeCountdown responseId={responseId} />
      ) : (
        <p className="opacity-70">{t('publicSurvey.prizeReveal.claimHint')}</p>
      )}
      {redirectMode === 'branded' ? (
        <p className="text-sm opacity-70">
          {t('publicSurvey.prizeReveal.brandedThankYou')}
        </p>
      ) : (
        <button
          type="button"
          onClick={onContinue}
          className="rounded-full px-8 py-3 text-base font-bold"
          style={{ backgroundColor: accent, color: primary }}
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}
